import type { FormRequest } from './validationSchema';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getEnvConfig } from '@configs/env';
import styled from '@emotion/styled';
import { Box, MenuItem, Modal, Select, TextField, useTheme } from '@mui/material';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { Button, Label } from '@components/index';
import { FileUpload, PDFPreviewModal } from '@pages/customer/common/file';
import authStore from '@stores/AuthStore';
import uiStore from '@stores/UiStore';
import { fonts } from '@theme/fontsCustomer';
import { ModalCheckIcon } from '@icons/ModalCheckIcon';
import { XIcon } from '@icons/XIcon';
import type { SubscriptionRequestCreateDTO } from '@repositories/subscriptionRequestRepository/subscriptionRequest.types';
import type { FileAttachment, PdfFilePreview } from '@customHooks/useFileDialog';
import { useAddSubscriptionRequest } from '@queryHooks/index';
import { useGetProductPlanList, useGetSoftware } from '@queryHooks/useSoftware';
import type { SubscriptionRequestModel } from '@repositories/subscriptionRequestRepository/models/SubscriptionRequestModel';
import { useItAdminFormOptions } from './validationSchema';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  swId: string;
  formData?: SubscriptionRequestModel;
}

export const PurchaseRequestModal = ({ isOpen, onClose, swId, formData }: Props) => {
  const { t } = useTranslation();
  const env = getEnvConfig();
  const { data: sw } = useGetSoftware(swId, { enabled: isOpen });
  const { data: plans } = useGetProductPlanList(swId, { enabled: isOpen });

  // 플랜 목록이 있는지 조회 ( 플랜이 없으면 선택 불가 )
  const hasPlanList = !!plans?.length && plans?.length > 0;

  const { mutate: applySubscription, isPending } = useAddSubscriptionRequest();
  const theme = useTheme();
  const { register, handleSubmit, reset, control, setValue } = useItAdminFormOptions();

  // 첨부파일 관리
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [pdfPreview, setPdfPreview] = useState<PdfFilePreview>({
    open: false,
    file: null,
    fileName: '',
    fileId: null,
    numPages: 0,
  });

  const validation = {
    success: (data: FormRequest) => {
      const rqData: SubscriptionRequestCreateDTO = {
        subscriptionRequests: [
          {
            softwareId: sw.id,
            inquiry: data.inquiry,
            productPlanId: data.productPlanId,
            requester: {
              name: data.requester,
            },
            files: data.files,
          },
        ],
        tenantId: authStore.curTenant.id,
      };
      applySubscription(rqData, {
        onSuccess: () => {
          uiStore.toastStore.open({
            message: t('Ad_Store_Purchase_Request_Complete'),
          });
          reset({}, { keepValues: false });
          setAttachments([]);
          setPdfPreview({
            open: false,
            file: null,
            fileId: null,
            fileName: '',
            numPages: 0,
          });
          onClose();
        },
        onError: error => {
          console.error('🚀 ~ PurchaseRequestModal ~ onError:', error);
          uiStore.toastStore.open({
            message: t('Ad_Store_Purchase_Request_Failed'),
          });
        },
      });
    },
  };

  const handleChangePdfPreview = (pdfPreview: PdfFilePreview) => {
    setPdfPreview(pdfPreview);
  };

  const handleChangeAttachments = (attachments: FileAttachment[]) => {
    setAttachments(attachments);
    setValue(
      'files',
      attachments.map(attachment => attachment.file),
    );
  };

  useEffect(() => {
    if (formData) {
      reset({
        softwareId: sw.id,
        inquiry: formData?.inquiry,
        productPlanId: formData.software.id,
      });
    }
  }, [formData, reset, sw.id]);

  const MenuProps = {
    PaperProps: {
      style: {
        width: 424,
        marginTop: 7,
        boxShadow: 'none',
        borderRadius: '0 0 2px 2px',
      },
    },
  };

  return (
    <Modal open onClose={onClose} sx={{ '& .MuiBackdrop-root': { background: 'transparent' } }}>
      <ModalWrapper onSubmit={handleSubmit(validation.success)}>
        <Header>
          {t('Subscrib_Detail_BuyRequest_09')}
          <IconButton onClick={onClose}>
            <XIcon width={24} height={24} color={theme.colors['ic-gray-main']} />
          </IconButton>
        </Header>
        <Body>
          <Element className="info-banner">
            <ModalCheckIcon width={16} height={16} color={theme.colors['ic-primary-light']} />
            <p>{t('Subscrib_Detail_BuyRequest_10', { providerName: t(`Provider_${env.SMP_PROVIDER_NAME}`) })}</p>
          </Element>
          <Element>
            <strong className="name">
              {t('Subscrib_Detail_UseRequest_07')}
              <span>*</span>
            </strong>
            <div className="software_name">
              <span>{sw.name}</span>
            </div>
          </Element>
          <Element>
            <strong className="name">
              {t('Subscrib_Main_12')}
              <span>*</span>
            </strong>
            <Controller
              control={control}
              name="productPlanId"
              render={({ field: { onChange, value = '' }, fieldState: { error } }) => (
                <>
                  <StyledSelect
                    size="large"
                    fullWidth
                    displayEmpty
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    disabled={!hasPlanList}
                    renderValue={(value: unknown) => {
                      const findPlanIdInfor = plans?.find(plan => plan.planId === value);
                      return value !== '' ? (
                        <>
                          <SelectedTitle>{findPlanIdInfor?.planName}</SelectedTitle>
                          {findPlanIdInfor?.isInUseByTenant ? (
                            <Label className="usebytenant" variant="contain" color="gradient">
                              {t('Ad_Store_Product_State')}
                            </Label>
                          ) : null}
                        </>
                      ) : (
                        <strong>{hasPlanList ? t('Product_Use_Request_02') : t('Product_Use_Request_19')}</strong>
                      );
                    }}
                    MenuProps={MenuProps}
                  >
                    {plans?.map(plan => {
                      return (
                        <SelectItem key={plan.planId} value={plan.planId}>
                          {plan.planName}
                          {plan.isInUseByTenant ? (
                            <Label variant="contain" color="gradient">
                              {t('Ad_Store_Product_State')}
                            </Label>
                          ) : null}
                        </SelectItem>
                      );
                    })}
                  </StyledSelect>
                  {error?.message && <ErrorText>{t(error.message)}</ErrorText>}
                </>
              )}
            />
          </Element>
          <Element>
            <strong className="name">{t('Acc_Main_36')}</strong>
            <div className="requester-text-field">
              <RequesterTextField
                {...register('requester')}
                placeholder={`${t('Acc_Main_36_Placeholder')}`}
                fullWidth
                size="medium"
              />
            </div>
          </Element>
          <Element>
            <strong className="name">{t('Purchase_History.Processing_Attachment')}</strong>
            <FileUpload
              attachments={attachments}
              updateAttachments={handleChangeAttachments}
              updatePdfPreview={handleChangePdfPreview}
            />
          </Element>
          <Element>
            <strong className="name">{t('Acc_Main_22')}</strong>
            <StyledTextField
              {...register('inquiry')}
              multiline
              rows={7}
              placeholder={`${t('Ad_Store_Purchase_Request_01')}`}
            />
          </Element>
        </Body>
        <SubmitButton isPending={isPending} variant="contain" size="extraLarge" type="submit" showLoadingIcon>
          {t('Ad_Store_Purchase_Request_03')}
        </SubmitButton>
        <PDFPreviewModal pdfPreview={pdfPreview} updatePdfPreview={handleChangePdfPreview} />
      </ModalWrapper>
    </Modal>
  );
};

const SelectedTitle = styled.strong`
  & ~ span {
    margin-left: 4px;
  }
`;

const ModalWrapper = styled.form`
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 480px;
  background-color: ${({ theme: { colors } }) => colors['bg-white']};
  border-radius: 6px;
  overflow: hidden;
  z-index: 1050;
  box-shadow: 0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04),
    0px 6px 30px 5px rgba(0, 0, 0, 0.05);
`;

const Header = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: inherit;
  padding: 18px 20px 16px 28px;
  ${fonts.Headline6};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const Body = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 4px 28px 0;
  width: 100%;
  overflow-y: auto;
  max-height: 585px;
`;

const Element = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  &:first-of-type {
    margin-bottom: 16px;
  }
  &.info-banner {
    align-items: center;
    justify-content: flex-start;
    flex-direction: initial;
    gap: 6px;
    background-color: ${({ theme }) => theme.colors['bg-primary-lighter']};
    border-radius: 3px;
    padding: 12px 20px;
    & > p {
      ${fonts.Body2};
      color: ${({ theme }) => theme.colors['text-primary']};
    }
  }
  & .name {
    ${fonts.Headline8};
    margin-bottom: 6px;
    color: ${({ theme: { colors } }) => colors['text-gray-main']};
    & > span {
      margin-left: 2px;
      color: ${({ theme: { colors } }) => colors['ic-red-light']};
    }
  }
  & .software_name {
    padding: 10px 12px;
    background-color: ${({ theme: { colors } }) => colors['bg-gray-light']};
    border: 1px solid ${({ theme: { colors } }) => colors['border-gray-light']};
    height: 48px;
    border-radius: 2px;
    span {
      ${fonts.Body2};
      color: ${({ theme: { colors } }) => colors['text-gray-main']};
    }
  }
  &:last-of-type {
    margin-bottom: 30px;
  }
  & .MuiInputBase-root textarea::placeholder {
    ${fonts.Body2};
    color: ${({ theme }) => theme.colors['text-gray-light']};
  }
  & .MuiSelect-select strong {
    ${fonts.Button4};
    color: ${({ theme }) => theme.colors['text-gray-main']};
  }
  & .requester-text-field {
    display: flex;
    width: 100%;
  }
`;
const StyledTextField = styled(TextField)`
  background: ${({ theme }) => theme.colors['bg-gray-lighter']};
  & .MuiInputBase-root {
    padding: 10px 12px 21px;
  }
`;

const SubmitButton = styled(SecureSubmitButton)`
  width: 100%;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  border-image-slice: 1;
  border-radius: 0;
`;

const SelectItem = styled(MenuItem)`
  gap: 4px;
  padding: 10px 10px 10px 7px;
`;

const IconButton = styled(Button)`
  width: 24px;
  height: 24px;
`;

const RequesterTextField = styled(TextField)`
  border-radius: 2px;
  .MuiInputBase-root {
    height: 48px;
  }
`;

const ErrorText = styled.p`
  ${fonts.Body4};
  color: ${({ theme }) => theme.colors['text-red-light']};
  margin-top: 4px;
  margin-bottom: 0;
`;

const StyledSelect = styled(Select)`
  &.Mui-disabled {
    background-color: ${({ theme: { colors } }) => colors['bg-gray-light']};
    color: ${({ theme: { colors } }) => colors['text-gray-lighter']};
    -webkit-text-fill-color: ${({ theme: { colors } }) => colors['text-gray-lighter']} !important;
  }
`;
